import React from "react";
import { Text, Card } from "@jsq/ds/core";
import { Money } from "~/components/Money";
import { type GetPaymentBatchByIdQuery } from "~/graphql/supergraphTypes";
import { toDbId } from "~/utils/graphql-helpers";
import {
  FidesProviderStatus,
  type FidesProviderStatusType,
} from "../../QueryProcessingPayments/ProcessingPaymentCard/FidesProviderStatus";
import { ModernTreasuryProviderStatus } from "../../QueryProcessingPayments/ProcessingPaymentCard/ModernTreasuryProviderStatus";
import { FieldRow } from "../../utils/FieldRow";
import { formatFieldValue } from "../../utils/formatFieldValue";
import * as styles from "./styles_vanilla.css";

// Types
type PaymentBatchType = NonNullable<GetPaymentBatchByIdQuery["paymentBatch"]>;
type FundingPaymentType = PaymentBatchType["fundingPayment"];
type PaymentType = PaymentBatchType["payments"][0];

// Union type for both payment types
type PaymentData = FundingPaymentType | PaymentType;

interface PaymentCardProps {
  payment: PaymentData;
  cardTitle: string;
  paymentIndex?: number;
}

// Type guards to determine payment type
const isFundingPayment = (payment: PaymentData): payment is FundingPaymentType => {
  return payment !== null && "bankAccountId" in payment;
};

const isRegularPayment = (payment: PaymentData): payment is PaymentType => {
  return payment !== null && "paymentPref" in payment;
};

// Type guard for provider status - checks for adminProviderStatus field existence
const hasAdminProviderStatus = (
  payment: PaymentData,
): payment is PaymentType & { adminProviderStatus: unknown } => {
  return isRegularPayment(payment) && "adminProviderStatus" in payment;
};

// Type guards for specific provider status types
const isFidesProviderStatus = (status: unknown): status is FidesProviderStatusType => {
  return (
    typeof status === "object" &&
    status !== null &&
    "__typename" in status &&
    status.__typename === "FidesProviderStatusDto"
  );
};

const isModernTreasuryProviderStatus = (status: unknown): boolean => {
  return (
    typeof status === "object" &&
    status !== null &&
    "__typename" in status &&
    status.__typename === "ModernTreasuryProviderStatusDto"
  );
};

const PaymentCard: React.FC<PaymentCardProps> = ({ payment, cardTitle, paymentIndex }) => {
  if (!payment) return null;

  const displayTitle = paymentIndex !== undefined ? `${cardTitle} ${paymentIndex}` : cardTitle;

  return (
    <Card key={payment.id} className={styles.resultCard}>
      <Text variant="regular-lg" className={styles.cardTitle}>
        {displayTitle}
      </Text>
      <div className={styles.cardContent}>
        {/* Common fields for both payment types */}
        <FieldRow
          label="ID:"
          value={formatFieldValue(payment.id)}
          fieldRowClassName={styles.fieldRow}
          fieldLabelClassName={styles.fieldLabel}
          fieldValueClassName={styles.fieldValue}
        />

        <FieldRow
          label="Status:"
          value={formatFieldValue(payment.status)}
          fieldRowClassName={styles.fieldRow}
          fieldLabelClassName={styles.fieldLabel}
          fieldValueClassName={styles.fieldValue}
        />

        {/* Regular payment specific fields */}
        {isRegularPayment(payment) && (
          <>
            <FieldRow
              label="Amount:"
              value={<Money money={payment.amount} rounded={false} />}
              fieldRowClassName={styles.fieldRow}
              fieldLabelClassName={styles.fieldLabel}
              fieldValueClassName={styles.fieldValue}
            />

            <FieldRow
              label="Object Type:"
              value={formatFieldValue(payment.objectType)}
              fieldRowClassName={styles.fieldRow}
              fieldLabelClassName={styles.fieldLabel}
              fieldValueClassName={styles.fieldValue}
            />

            <FieldRow
              label="Object ID:"
              value={formatFieldValue(toDbId(payment.objectId))}
              fieldRowClassName={styles.fieldRow}
              fieldLabelClassName={styles.fieldLabel}
              fieldValueClassName={styles.fieldValue}
            />
          </>
        )}

        <FieldRow
          label="Direction:"
          value={formatFieldValue(payment.direction)}
          fieldRowClassName={styles.fieldRow}
          fieldLabelClassName={styles.fieldLabel}
          fieldValueClassName={styles.fieldValue}
        />

        <FieldRow
          label="Method:"
          value={formatFieldValue(payment.method)}
          fieldRowClassName={styles.fieldRow}
          fieldLabelClassName={styles.fieldLabel}
          fieldValueClassName={styles.fieldValue}
        />

        <FieldRow
          label="Transmitted At:"
          value={formatFieldValue(payment.transmittedAt)}
          fieldRowClassName={styles.fieldRow}
          fieldLabelClassName={styles.fieldLabel}
          fieldValueClassName={styles.fieldValue}
        />

        {/* Regular payment specific fields */}
        {isRegularPayment(payment) && (
          <>
            <FieldRow
              label="Settled At:"
              value={formatFieldValue(payment.settledAt)}
              fieldRowClassName={styles.fieldRow}
              fieldLabelClassName={styles.fieldLabel}
              fieldValueClassName={styles.fieldValue}
            />

            <FieldRow
              label="Failed At:"
              value={formatFieldValue(payment.failedAt)}
              fieldRowClassName={styles.fieldRow}
              fieldLabelClassName={styles.fieldLabel}
              fieldValueClassName={styles.fieldValue}
            />
          </>
        )}

        <FieldRow
          label="Scheduled Process Date:"
          value={formatFieldValue(payment.scheduledProcessDate)}
          fieldRowClassName={styles.fieldRow}
          fieldLabelClassName={styles.fieldLabel}
          fieldValueClassName={styles.fieldValue}
        />

        {/* Funding payment specific field */}
        {isFundingPayment(payment) && (
          <FieldRow
            label="Bank Account ID:"
            value={
              payment.bankAccountId ? formatFieldValue(toDbId(payment.bankAccountId)) : "null"
            }
            fieldRowClassName={styles.fieldRow}
            fieldLabelClassName={styles.fieldLabel}
            fieldValueClassName={styles.fieldValue}
          />
        )}

        {/* Regular payment specific field */}
        {isRegularPayment(payment) && (
          <FieldRow
            label="Payment Pref ID:"
            value={
              payment.paymentPref ? formatFieldValue(toDbId(payment.paymentPref.id)) : "null"
            }
            fieldRowClassName={styles.fieldRow}
            fieldLabelClassName={styles.fieldLabel}
            fieldValueClassName={styles.fieldValue}
          />
        )}

        {/* Common fields for both payment types */}
        <FieldRow
          label="Failure Reason:"
          value={formatFieldValue(payment.failureReason)}
          fieldRowClassName={styles.fieldRow}
          fieldLabelClassName={styles.fieldLabel}
          fieldValueClassName={styles.fieldValue}
        />

        <FieldRow
          label="Return Code:"
          value={formatFieldValue(payment.returnCode)}
          fieldRowClassName={styles.fieldRow}
          fieldLabelClassName={styles.fieldLabel}
          fieldValueClassName={styles.fieldValue}
        />

        <FieldRow
          label="Return Reason:"
          value={formatFieldValue(payment.returnReason)}
          fieldRowClassName={styles.fieldRow}
          fieldLabelClassName={styles.fieldLabel}
          fieldValueClassName={styles.fieldValue}
        />

        {/* Provider status section for regular payments */}
        {hasAdminProviderStatus(payment) &&
          isFidesProviderStatus(payment.adminProviderStatus) && (
            <FidesProviderStatus status={payment.adminProviderStatus} />
          )}
        {hasAdminProviderStatus(payment) &&
          isModernTreasuryProviderStatus(payment.adminProviderStatus) && (
            <ModernTreasuryProviderStatus />
          )}
      </div>
    </Card>
  );
};

export default PaymentCard;
export { PaymentCard };
export type { PaymentCardProps, PaymentData };
